import * as React from "react";

import "./confirm.scss";

import {
    ComponentLink,
    Link,
    makeChain,
    valueHelper as value,
}                                from "@Common/react/cst2";
import {Load2}                   from "@Common/react/load2";
import {UseState}                from "@Common/react/use-state";
import {Types}                   from "@Common/types";
import {addURLParameterToWindow} from "@Common/utils/add-url-parameter";
import {isEmpty}                 from "@Common/utils/objects";

import {InternalLink}   from "@Xpresso/routes/common/components/internal-link";
import {consumeContext} from "@Xpresso/loaders/consume-context";
import {Layout}         from "@Xpresso/routes/layout/layout";

import {OfficeSelector} from "./office-selector";

export const Confirm = () => (
    makeChain<JSX.Element>()
    .then(...consumeContext("apis"))
    .then(...consumeContext("themeScheme"))
    .then(...consumeContext("routerHistory"))
    .then("confirmData", ({apis}, next:ComponentLink<typeof Load2<Types.Apis.Payloads.Next.Authenticate.Data.Response>>) => Load2({
        _key: "confirmData",
        fetch: () => apis.next.authenticate.data(),
        next,
    }))
    .then("submitting", (_, next:ComponentLink<typeof UseState<boolean>>) => UseState({
        _key: "submitting",
        defaultValue: false,
        next,
    }))
    .then("error", (_, next:ComponentLink<typeof UseState<{
        statusCode?: number, message?: string, internalMessage?:string
    }>>) => UseState({
        _key: "error",
        defaultValue: {},
        next,
    }))
    .then("match", value(({confirmData}) => confirmData.value?.accountMatch ))
    .then("newUser", value(({confirmData}) => confirmData.value?.newUser ))
    .then("branchSelection", value(({confirmData}) => confirmData.value?.branchSelection ))
    .then("handleResponse", ({error, themeScheme, routerHistory}, next:Link<JSX.Element, ({result}) => void>) => next( async ({result}) => {
        if(result?.statusCode !== "success") {
            addURLParameterToWindow({parameter:"error", value: result.statusCode})
            error.onChange({...result})
        } else {
            if(result?.data?.rl_xd_session) {
                const form = document.createElement('form');
                form.method = 'POST';
                form.action = result.data.redirectUrl;
                const input = document.createElement('input');
                input.type = 'hidden';
                input.name = 'rl_xd_session';
                input.value = result.data.rl_xd_session;
                form.appendChild(input);
                document.body.appendChild(form);
                form.submit();
            } else if(result?.data?.redirectUrl) {
                let redirect = result?.data.redirectUrl;
                if (redirect?.includes('/next/')) {
                    window.location.replace(redirect);
                } else {
                    redirect = redirect === "index.php" ? "/" : redirect;
                    redirect = redirect.replace(/.*\/app/, "");
                    await themeScheme.reload();
                    routerHistory.replace(redirect);
                }
            }
        }
    }))
    .then(({
        apis,
        match,
        confirmData,
        newUser,
        branchSelection,
        submitting,
        error,
        handleResponse,
    }, next:Link<void>) => (
    <Layout className="confirm-6p9">
        {!isEmpty(error.value) ? (
            <div className="error-card" xd-qa-type="card" xd-qa-name="Error">
                <div className="header">
                    <div className="icon" xd-qa-type="icon" xd-qa-name="Alert">
                        <img alt="error" src={require("../common/alert.svg")}/>
                    </div>
                    <div className="code" xd-qa-type="label" xd-qa-name="Code">{`Error Code: ${error.value?.statusCode}`}</div>
                </div>
                <div className="body">
                    <div className="message" xd-qa-type="label" xd-qa-name="Message">
                        {error.value?.message}
                    </div>
                    {error.value?.internalMessage && <div className="internal-message" xd-qa-type="label" xd-qa-name="Internal Message">
                        Internal Message:<br/>
                        <pre>{JSON.stringify(error.value?.internalMessage, null, 4)}</pre>
                    </div>}
                </div>
            </div>
        ) : confirmData?.value && newUser?.userId ? (<>
            <div className="header">
                <div className="title" xd-qa-type="label" xd-qa-name="Title">
                    User Confirmation
                </div>
            </div>
            <hr></hr>
            {confirmData?.value && <div className="header">
                <div className="title" xd-qa-type="label" xd-qa-name="header">
                    {match?.userId ? "We found an account that may belong to you." : `Welcome, ${newUser?.firstName || "User"}!`}
                </div>
                <div className="title" xd-qa-type="label" xd-qa-name="header">
                    {match?.userId ? "Is this you?" : "Everything look good?"}
                </div>
            </div>}
            {newUser?.userId && <div className="columns">
                {match?.userId && <div className="column" xd-qa-type="card" xd-qa-name="Existing Account Info">
                    <div className="title" xd-qa-type="label" xd-qa-name="Column Title">
                        Existing Account Info
                    </div>
                    <div className="card">
                        <div className="title" xd-qa-type="label" xd-qa-name="Name">
                            {match?.userName}
                        </div>
                        <div className="subTitle" xd-qa-type="label" xd-qa-name="Email">
                            {match?.userEmail}
                        </div>
                        <div className="subTitle" xd-qa-type="label" xd-qa-name="Link Id">
                            {match?.userId}
                        </div>
                        <div className="office">
                            Office
                        </div>
                        <div className="officeAddress" xd-qa-type="label" xd-qa-name="Office Name">
                            {match?.officeName}
                        </div>
                        <div className="officeAddress" xd-qa-type="label" xd-qa-name="Office Address Line 1">
                            {match?.officeAddress1}
                        </div>
                        {match?.officeAddress2 && (
                            <div className="officeAddress" xd-qa-type="label" xd-qa-name="Office Address Line 2">
                                {match?.officeAddress2}
                            </div>
                        )}
                        {match?.officeCity && (
                            <div className="officeAddress" xd-qa-type="label" xd-qa-name="Office Address Line 3">
                                {`${match?.officeCity}, ${match?.officeState} ${match?.officeZip} ${match?.officeCountry}` }
                            </div>
                        )}
                    </div>
                    <button
                        className="primary"
                        disabled={submitting.value}
                        onClick={async () => {
                            submitting.onChange(true);
                            try{
                                const result = await apis.next.authenticate.agentConfirm({idAgent: match?.idAgent})
                                handleResponse({result})
                            } catch (e) {
                                const result = await e.json()
                                if(result?.statusCode)
                                {
                                    handleResponse({result})
                                }
                                else {
                                    alert("Something went wrong!")
                                }
                            }
                            submitting.onChange(false);
                        }}
                        xd-qa-type="button"
                        xd-qa-name="Merge"
                    >
                        Yes, Use this account
                    </button>
                </div>}
                <div className="column" xd-qa-type="card" xd-qa-name="New Account Info">
                    {match?.userId && <div className="title" xd-qa-type="label" xd-qa-name="Column Title">
                        New Account Info
                    </div>}
                    <div className="card">
                        <div className="title" xd-qa-type="label" xd-qa-name="Name">
                            {newUser?.userName}
                        </div>
                        <div className="subTitle" xd-qa-type="label" xd-qa-name="Email">
                            {newUser?.userEmail}
                        </div>
                        <div className="subTitle" xd-qa-type="label" xd-qa-name="Link Id">
                            {newUser?.userId}
                        </div>
                        <div className="office">
                            Office
                        </div>
                        <div className="officeAddress" xd-qa-type="label" xd-qa-name="Office Name">
                            {newUser?.officeName}
                        </div>
                        <div className="officeAddress" xd-qa-type="label" xd-qa-name="Office Address Line 1">
                            {newUser?.officeAddress1}
                        </div>
                        {newUser?.officeAddress2 && (
                            <div className="officeAddress" xd-qa-type="label" xd-qa-name="Office Address Line 2">
                                {newUser?.officeAddress2}
                            </div>
                        )}
                        {newUser?.officeCity && (
                            <div className="officeAddress" xd-qa-type="label" xd-qa-name="Office Address Line 3">
                                {`${newUser?.officeCity}, ${newUser?.officeState} ${newUser?.officeZip} ${newUser?.officeCountry}` }
                            </div>
                        )}
                    </div>
                    <button
                        className="primary"
                        disabled={submitting.value}
                        onClick={async () => {
                            submitting.onChange(true);
                            try{
                                const result = await apis.next.authenticate.agentNew()
                                handleResponse({result})
                            } catch (e) {
                                const result = await e.json()
                                if(result?.statusCode)
                                {
                                    handleResponse({result})
                                }
                                else {
                                    alert("Something went wrong!")
                                }
                            }
                            submitting.onChange(false);
                        }}
                        xd-qa-type="button"
                        xd-qa-name="Login"
                    >
                        {match?.userId ? "No, Create a new account" : "Yes, This looks correct"}
                    </button>
                </div>
            </div>}
            <hr></hr>
            <div className="header">
                <div className="title" xd-qa-type="label" xd-qa-name="Sub Title">
                    Have an issue?
                </div>
                <InternalLink className="link" to="/support" xd-qa-type="link" xd-qa-name="Contact Us">Contact Us</InternalLink>
            </div>
            </>) : branchSelection?.branches?.length > 0 || branchSelection?.newBranches?.length > 0 ? (<>
                <div className="header">
                    <div className="title" xd-qa-type="label" xd-qa-name="Title">
                        Office Selection
                    </div>
                    <div className="subTitle" xd-qa-type="label" xd-qa-name="Sub Title">
                        Have an issue?
                    </div>
                    <InternalLink className="link" to="/support" xd-qa-type="link" xd-qa-name="Contact Us">Contact Us</InternalLink>
                </div>
                <hr></hr>
                {branchSelection?.isNewBranchUser && (
                    <div className="header">
                        <div className="title" xd-qa-type="label" xd-qa-name="Title New Branch User">
                            Welcome. Please find your office in the list below and click "Login". An account will be created for you automatically in that office.
                        </div>
                    </div>
                )}
                {branchSelection?.branches?.length > 0 && OfficeSelector({handleResponse, offices: branchSelection?.branches, submitting})}
                {branchSelection?.newBranches?.length > 0 && (<>
                    <div className="header">
                        <div className="titleNew" xd-qa-type="label" xd-qa-name="Title New">
                            Your profile contains these office IDs which are not configured in this platform
                        </div>
                    </div>
                    {OfficeSelector({handleResponse, offices: branchSelection?.newBranches, newBranches: true})}
                </>)}
            </>) : confirmData?.value && (<>
                Something went wrong! Please go back and try this action again.
            </>)}
    </Layout>
    ))
    .run()
);
